import { frFR, idID, jaJP, koKR, msMY, thTH, viVN, zhTW } from '@clerk/localizations';
import type { SupportedLocale } from './types';

/**
 * Clerk doesn't ship en-AU — English is the default, no base locale needed.
 * zh-HK: Clerk has zhTW (Traditional Chinese) as the closest match.
 * fr-CA: Clerk has frFR — close enough for Canadian French Clerk UI.
 */
const CLERK_BASE_LOCALES: Partial<Record<SupportedLocale, Record<string, any>>> = {
  'fr-CA': frFR,
  'id-ID': idID,
  'ja-JP': jaJP,
  'ko-KR': koKR,
  'ms-MY': msMY,
  'th-TH': thTH,
  'vi-VN': viVN,
  'zh-HK': zhTW,
};

/**
 * Firmable-specific overrides per locale.
 * These replace "Organization" with "Company" terminology and customize
 * form labels, error messages, sign-in/sign-up copy, etc.
 */
const FIRMABLE_OVERRIDES: Record<string, Record<string, any>> = {
  en: {
    createOrganization: {
      title: 'Create company',
      formButtonSubmit: 'Create',
    },
    formButtonPrimary: 'Continue',
    formFieldLabel__organizationSlug:
      'Company unique identifier (This is your unique company identifier, please do not change it.)',
    formFieldInputPlaceholder__organizationSlug: 'Something to identify your company uniquely',
    formFieldInputPlaceholder__organizationName: 'Company name',
    formFieldLabel__organizationName: 'Company name',
    unstable__errors: {
      form_identifier_exists__phone_number: 'This mobile number is taken. Please try another.',
      form_param_format_invalid__phone_number: 'Mobile number must be in a valid international format',
      not_allowed_access: 'Your account has been temporarily locked. Contact support to find out more.',
      user_banned: 'Your account has been temporarily locked. Contact support to find out more.',
    },
    formFieldInputPlaceholder__phoneNumber: '412 345 678',
    organizationList: {
      titleWithoutPersonal: 'Join your company',
      subtitle: 'Request to join an existing company or create a new one',
      action__createOrganization: 'Create a new company',
      action__suggestionsAccept: 'Request to join',
    },
    organizationProfile: {
      navbar: { title: 'Company', description: 'Manage your company', members: 'Users' },
      start: { headerTitle__members: 'Users', profileSection: { title: 'Company profile' } },
      profilePage: {
        title: 'Company profile',
        dangerSection: {
          leaveOrganization: {
            title: 'Leave company',
            messageLine1:
              'Are you sure you want to leave this company? You will lose access to the Firmable app.',
            messageLine2: 'This action is permanent and cannot be undone.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Users' },
        activeMembersTab: { menuAction__remove: 'Remove user' },
        action__invite: 'Invite user',
      },
      invitePage: { title: 'Invite new users' },
    },
    signIn: {
      start: { title: 'Log in', subtitle: 'to continue to Firmable', actionLink__use_phone: 'Use mobile number' },
    },
    signUp: { start: { subtitle: 'to continue to Firmable', actionLink: 'Log in' } },
    formFieldLabel__emailAddress: 'Work email',
    formFieldLabel__phoneNumber: 'Mobile number',
    roles: { basic_member: 'Standard' },
  },

  'fr-CA': {
    createOrganization: { title: "Creer une entreprise", formButtonSubmit: 'Creer' },
    formButtonPrimary: 'Continuer',
    formFieldLabel__organizationSlug:
      "Identifiant unique de l'entreprise (Ceci est l'identifiant unique de votre entreprise, veuillez ne pas le modifier.)",
    formFieldInputPlaceholder__organizationSlug: 'Quelque chose pour identifier votre entreprise de maniere unique',
    formFieldInputPlaceholder__organizationName: "Nom de l'entreprise",
    formFieldLabel__organizationName: "Nom de l'entreprise",
    unstable__errors: {
      form_identifier_exists__phone_number: "Ce numero de mobile est deja utilise. Veuillez en essayer un autre.",
      form_param_format_invalid__phone_number: 'Le numero de mobile doit etre dans un format international valide',
      not_allowed_access: 'Votre compte a ete temporairement verrouille. Contactez le support pour en savoir plus.',
      user_banned: 'Votre compte a ete temporairement verrouille. Contactez le support pour en savoir plus.',
    },
    formFieldInputPlaceholder__phoneNumber: '412 345 678',
    organizationList: {
      titleWithoutPersonal: 'Rejoindre votre entreprise',
      subtitle: 'Demandez a rejoindre une entreprise existante ou creez-en une nouvelle',
      action__createOrganization: 'Creer une nouvelle entreprise',
      action__suggestionsAccept: 'Demander a rejoindre',
    },
    organizationProfile: {
      navbar: { title: 'Entreprise', description: 'Gerer votre entreprise', members: 'Utilisateurs' },
      start: { headerTitle__members: 'Utilisateurs', profileSection: { title: "Profil de l'entreprise" } },
      profilePage: {
        title: "Profil de l'entreprise",
        dangerSection: {
          leaveOrganization: {
            title: "Quitter l'entreprise",
            messageLine1:
              "Etes-vous sur de vouloir quitter cette entreprise ? Vous perdrez l'acces a l'application Firmable.",
            messageLine2: 'Cette action est permanente et ne peut pas etre annulee.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Utilisateurs' },
        activeMembersTab: { menuAction__remove: "Supprimer l'utilisateur" },
        action__invite: 'Inviter un utilisateur',
      },
      invitePage: { title: 'Inviter de nouveaux utilisateurs' },
    },
    signIn: {
      start: {
        title: 'Se connecter',
        subtitle: 'pour continuer vers Firmable',
        actionLink__use_phone: 'Utiliser le numero de mobile',
      },
    },
    signUp: { start: { subtitle: 'pour continuer vers Firmable', actionLink: 'Se connecter' } },
    formFieldLabel__emailAddress: 'Courriel professionnel',
    formFieldLabel__phoneNumber: 'Numero de mobile',
    roles: { basic_member: 'Standard' },
  },

  'id-ID': {
    createOrganization: { title: 'Buat perusahaan', formButtonSubmit: 'Buat' },
    formButtonPrimary: 'Lanjutkan',
    formFieldLabel__organizationSlug:
      'Pengidentifikasi unik perusahaan (Ini adalah pengidentifikasi unik perusahaan Anda, harap jangan mengubahnya.)',
    formFieldInputPlaceholder__organizationSlug: 'Sesuatu untuk mengidentifikasi perusahaan Anda secara unik',
    formFieldInputPlaceholder__organizationName: 'Nama perusahaan',
    formFieldLabel__organizationName: 'Nama perusahaan',
    unstable__errors: {
      form_identifier_exists__phone_number: 'Nomor ponsel ini sudah digunakan. Silakan coba yang lain.',
      form_param_format_invalid__phone_number: 'Nomor ponsel harus dalam format internasional yang valid',
      not_allowed_access: 'Akun Anda telah dikunci sementara. Hubungi dukungan untuk informasi lebih lanjut.',
      user_banned: 'Akun Anda telah dikunci sementara. Hubungi dukungan untuk informasi lebih lanjut.',
    },
    organizationList: {
      titleWithoutPersonal: 'Bergabung dengan perusahaan Anda',
      subtitle: 'Minta untuk bergabung dengan perusahaan yang ada atau buat yang baru',
      action__createOrganization: 'Buat perusahaan baru',
      action__suggestionsAccept: 'Minta bergabung',
    },
    organizationProfile: {
      navbar: { title: 'Perusahaan', description: 'Kelola perusahaan Anda', members: 'Pengguna' },
      start: { headerTitle__members: 'Pengguna', profileSection: { title: 'Profil perusahaan' } },
      profilePage: {
        title: 'Profil perusahaan',
        dangerSection: {
          leaveOrganization: {
            title: 'Tinggalkan perusahaan',
            messageLine1:
              'Apakah Anda yakin ingin meninggalkan perusahaan ini? Anda akan kehilangan akses ke aplikasi Firmable.',
            messageLine2: 'Tindakan ini bersifat permanen dan tidak dapat dibatalkan.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Pengguna' },
        activeMembersTab: { menuAction__remove: 'Hapus pengguna' },
        action__invite: 'Undang pengguna',
      },
      invitePage: { title: 'Undang pengguna baru' },
    },
    signIn: {
      start: {
        title: 'Masuk',
        subtitle: 'untuk melanjutkan ke Firmable',
        actionLink__use_phone: 'Gunakan nomor ponsel',
      },
    },
    signUp: { start: { subtitle: 'untuk melanjutkan ke Firmable', actionLink: 'Masuk' } },
    formFieldLabel__emailAddress: 'Email kerja',
    formFieldLabel__phoneNumber: 'Nomor ponsel',
    roles: { basic_member: 'Standar' },
  },

  'ja-JP': {
    createOrganization: { title: '会社を作成', formButtonSubmit: '作成' },
    formButtonPrimary: '続行',
    formFieldLabel__organizationSlug:
      '会社固有識別子（これはあなたの会社の固有識別子です。変更しないでください。）',
    formFieldInputPlaceholder__organizationSlug: '会社を一意に識別するもの',
    formFieldInputPlaceholder__organizationName: '会社名',
    formFieldLabel__organizationName: '会社名',
    unstable__errors: {
      form_identifier_exists__phone_number: 'この携帯番号は既に使用されています。別の番号をお試しください。',
      form_param_format_invalid__phone_number: '携帯番号は有効な国際形式である必要があります',
      not_allowed_access: 'お客様のアカウントは一時的にロックされています。詳細はサポートまでお問い合わせください。',
      user_banned: 'お客様のアカウントは一時的にロックされています。詳細はサポートまでお問い合わせください。',
    },
    organizationList: {
      titleWithoutPersonal: '会社に参加',
      subtitle: '既存の会社への参加をリクエストするか、新しい会社を作成してください',
      action__createOrganization: '新しい会社を作成',
      action__suggestionsAccept: '参加をリクエスト',
    },
    organizationProfile: {
      navbar: { title: '会社', description: '会社を管理', members: 'ユーザー' },
      start: { headerTitle__members: 'ユーザー', profileSection: { title: '会社プロフィール' } },
      profilePage: {
        title: '会社プロフィール',
        dangerSection: {
          leaveOrganization: {
            title: '会社を退出',
            messageLine1:
              'この会社を退出してもよろしいですか？Firmableアプリへのアクセスを失います。',
            messageLine2: 'この操作は永久的で、元に戻すことはできません。',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'ユーザー' },
        activeMembersTab: { menuAction__remove: 'ユーザーを削除' },
        action__invite: 'ユーザーを招待',
      },
      invitePage: { title: '新しいユーザーを招待' },
    },
    signIn: {
      start: {
        title: 'ログイン',
        subtitle: 'Firmableに続行',
        actionLink__use_phone: '携帯番号を使用',
      },
    },
    signUp: { start: { subtitle: 'Firmableに続行', actionLink: 'ログイン' } },
    formFieldLabel__emailAddress: '会社メール',
    formFieldLabel__phoneNumber: '携帯番号',
    roles: { basic_member: 'スタンダード' },
  },

  'ko-KR': {
    createOrganization: { title: '회사 만들기', formButtonSubmit: '만들기' },
    formButtonPrimary: '계속',
    formFieldLabel__organizationSlug:
      '회사 고유 식별자 (이것은 회사의 고유 식별자입니다. 변경하지 마세요.)',
    formFieldInputPlaceholder__organizationSlug: '회사를 고유하게 식별할 수 있는 것',
    formFieldInputPlaceholder__organizationName: '회사 이름',
    formFieldLabel__organizationName: '회사 이름',
    unstable__errors: {
      form_identifier_exists__phone_number: '이 휴대폰 번호는 이미 사용 중입니다. 다른 번호를 시도해 주세요.',
      form_param_format_invalid__phone_number: '휴대폰 번호는 유효한 국제 형식이어야 합니다',
      not_allowed_access: '계정이 일시적으로 잠겼습니다. 자세한 내용은 지원팀에 문의하세요.',
      user_banned: '계정이 일시적으로 잠겼습니다. 자세한 내용은 지원팀에 문의하세요.',
    },
    organizationList: {
      titleWithoutPersonal: '회사에 가입',
      subtitle: '기존 회사에 가입을 요청하거나 새 회사를 만드세요',
      action__createOrganization: '새 회사 만들기',
      action__suggestionsAccept: '가입 요청',
    },
    organizationProfile: {
      navbar: { title: '회사', description: '회사 관리', members: '사용자' },
      start: { headerTitle__members: '사용자', profileSection: { title: '회사 프로필' } },
      profilePage: {
        title: '회사 프로필',
        dangerSection: {
          leaveOrganization: {
            title: '회사 탈퇴',
            messageLine1: '이 회사를 탈퇴하시겠습니까? Firmable 앱에 대한 액세스를 잃게 됩니다.',
            messageLine2: '이 작업은 영구적이며 되돌릴 수 없습니다.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: '사용자' },
        activeMembersTab: { menuAction__remove: '사용자 제거' },
        action__invite: '사용자 초대',
      },
      invitePage: { title: '새 사용자 초대' },
    },
    signIn: {
      start: {
        title: '로그인',
        subtitle: 'Firmable로 계속',
        actionLink__use_phone: '휴대폰 번호 사용',
      },
    },
    signUp: { start: { subtitle: 'Firmable로 계속', actionLink: '로그인' } },
    formFieldLabel__emailAddress: '업무 이메일',
    formFieldLabel__phoneNumber: '휴대폰 번호',
    roles: { basic_member: '스탠다드' },
  },

  'ms-MY': {
    createOrganization: { title: 'Buat syarikat', formButtonSubmit: 'Buat' },
    formButtonPrimary: 'Teruskan',
    formFieldLabel__organizationSlug:
      'Pengecam unik syarikat (Ini adalah pengecam unik syarikat anda, sila jangan ubahnya.)',
    formFieldInputPlaceholder__organizationSlug: 'Sesuatu untuk mengenal pasti syarikat anda secara unik',
    formFieldInputPlaceholder__organizationName: 'Nama syarikat',
    formFieldLabel__organizationName: 'Nama syarikat',
    unstable__errors: {
      form_identifier_exists__phone_number: 'Nombor telefon bimbit ini telah diambil. Sila cuba yang lain.',
      form_param_format_invalid__phone_number: 'Nombor telefon bimbit mesti dalam format antarabangsa yang sah',
      not_allowed_access: 'Akaun anda telah dikunci buat sementara waktu. Hubungi sokongan untuk maklumat lanjut.',
      user_banned: 'Akaun anda telah dikunci buat sementara waktu. Hubungi sokongan untuk maklumat lanjut.',
    },
    organizationList: {
      titleWithoutPersonal: 'Sertai syarikat anda',
      subtitle: 'Minta untuk menyertai syarikat sedia ada atau buat yang baharu',
      action__createOrganization: 'Buat syarikat baharu',
      action__suggestionsAccept: 'Minta untuk menyertai',
    },
    organizationProfile: {
      navbar: { title: 'Syarikat', description: 'Urus syarikat anda', members: 'Pengguna' },
      start: { headerTitle__members: 'Pengguna', profileSection: { title: 'Profil syarikat' } },
      profilePage: {
        title: 'Profil syarikat',
        dangerSection: {
          leaveOrganization: {
            title: 'Tinggalkan syarikat',
            messageLine1:
              'Adakah anda pasti mahu meninggalkan syarikat ini? Anda akan kehilangan akses kepada aplikasi Firmable.',
            messageLine2: 'Tindakan ini kekal dan tidak boleh dibatalkan.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Pengguna' },
        activeMembersTab: { menuAction__remove: 'Alih keluar pengguna' },
        action__invite: 'Jemput pengguna',
      },
      invitePage: { title: 'Jemput pengguna baharu' },
    },
    signIn: {
      start: {
        title: 'Log masuk',
        subtitle: 'untuk teruskan ke Firmable',
        actionLink__use_phone: 'Gunakan nombor telefon bimbit',
      },
    },
    signUp: { start: { subtitle: 'untuk teruskan ke Firmable', actionLink: 'Log masuk' } },
    formFieldLabel__emailAddress: 'E-mel kerja',
    formFieldLabel__phoneNumber: 'Nombor telefon bimbit',
    roles: { basic_member: 'Standard' },
  },

  'th-TH': {
    createOrganization: { title: 'สร้างบริษัท', formButtonSubmit: 'สร้าง' },
    formButtonPrimary: 'ดำเนินการต่อ',
    formFieldLabel__organizationSlug:
      'ตัวระบุเฉพาะของบริษัท (นี่คือตัวระบุเฉพาะของบริษัทคุณ กรุณาอย่าเปลี่ยนแปลง)',
    formFieldInputPlaceholder__organizationSlug: 'สิ่งที่ระบุบริษัทของคุณได้อย่างเฉพาะเจาะจง',
    formFieldInputPlaceholder__organizationName: 'ชื่อบริษัท',
    formFieldLabel__organizationName: 'ชื่อบริษัท',
    unstable__errors: {
      form_identifier_exists__phone_number: 'หมายเลขโทรศัพท์มือถือนี้ถูกใช้แล้ว กรุณาลองหมายเลขอื่น',
      form_param_format_invalid__phone_number: 'หมายเลขโทรศัพท์มือถือต้องอยู่ในรูปแบบสากลที่ถูกต้อง',
      not_allowed_access: 'บัญชีของคุณถูกล็อกชั่วคราว ติดต่อฝ่ายสนับสนุนเพื่อดูรายละเอียดเพิ่มเติม',
      user_banned: 'บัญชีของคุณถูกล็อกชั่วคราว ติดต่อฝ่ายสนับสนุนเพื่อดูรายละเอียดเพิ่มเติม',
    },
    organizationList: {
      titleWithoutPersonal: 'เข้าร่วมบริษัทของคุณ',
      subtitle: 'ขอเข้าร่วมบริษัทที่มีอยู่หรือสร้างบริษัทใหม่',
      action__createOrganization: 'สร้างบริษัทใหม่',
      action__suggestionsAccept: 'ขอเข้าร่วม',
    },
    organizationProfile: {
      navbar: { title: 'บริษัท', description: 'จัดการบริษัทของคุณ', members: 'ผู้ใช้' },
      start: { headerTitle__members: 'ผู้ใช้', profileSection: { title: 'โปรไฟล์บริษัท' } },
      profilePage: {
        title: 'โปรไฟล์บริษัท',
        dangerSection: {
          leaveOrganization: {
            title: 'ออกจากบริษัท',
            messageLine1:
              'คุณแน่ใจหรือไม่ว่าต้องการออกจากบริษัทนี้? คุณจะสูญเสียการเข้าถึงแอป Firmable',
            messageLine2: 'การดำเนินการนี้ถาวรและไม่สามารถยกเลิกได้',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'ผู้ใช้' },
        activeMembersTab: { menuAction__remove: 'ลบผู้ใช้' },
        action__invite: 'เชิญผู้ใช้',
      },
      invitePage: { title: 'เชิญผู้ใช้ใหม่' },
    },
    signIn: {
      start: {
        title: 'เข้าสู่ระบบ',
        subtitle: 'เพื่อดำเนินการต่อไปยัง Firmable',
        actionLink__use_phone: 'ใช้หมายเลขโทรศัพท์มือถือ',
      },
    },
    signUp: { start: { subtitle: 'เพื่อดำเนินการต่อไปยัง Firmable', actionLink: 'เข้าสู่ระบบ' } },
    formFieldLabel__emailAddress: 'อีเมลงาน',
    formFieldLabel__phoneNumber: 'หมายเลขโทรศัพท์มือถือ',
    roles: { basic_member: 'มาตรฐาน' },
  },

  'vi-VN': {
    createOrganization: { title: 'Tao cong ty', formButtonSubmit: 'Tao' },
    formButtonPrimary: 'Tiep tuc',
    formFieldLabel__organizationSlug:
      'Ma dinh danh duy nhat cua cong ty (Day la ma dinh danh duy nhat cua cong ty ban, vui long khong thay doi.)',
    formFieldInputPlaceholder__organizationSlug: 'Thong tin de nhan dien cong ty cua ban mot cach duy nhat',
    formFieldInputPlaceholder__organizationName: 'Ten cong ty',
    formFieldLabel__organizationName: 'Ten cong ty',
    unstable__errors: {
      form_identifier_exists__phone_number: 'So di dong nay da duoc su dung. Vui long thu so khac.',
      form_param_format_invalid__phone_number: 'So di dong phai o dinh dang quoc te hop le',
      not_allowed_access: 'Tai khoan cua ban da bi khoa tam thoi. Lien he bo phan ho tro de biet them chi tiet.',
      user_banned: 'Tai khoan cua ban da bi khoa tam thoi. Lien he bo phan ho tro de biet them chi tiet.',
    },
    organizationList: {
      titleWithoutPersonal: 'Tham gia cong ty cua ban',
      subtitle: 'Yeu cau tham gia cong ty hien co hoac tao cong ty moi',
      action__createOrganization: 'Tao cong ty moi',
      action__suggestionsAccept: 'Yeu cau tham gia',
    },
    organizationProfile: {
      navbar: { title: 'Cong ty', description: 'Quan ly cong ty cua ban', members: 'Nguoi dung' },
      start: { headerTitle__members: 'Nguoi dung', profileSection: { title: 'Ho so cong ty' } },
      profilePage: {
        title: 'Ho so cong ty',
        dangerSection: {
          leaveOrganization: {
            title: 'Roi cong ty',
            messageLine1:
              'Ban co chac chan muon roi cong ty nay khong? Ban se mat quyen truy cap vao ung dung Firmable.',
            messageLine2: 'Hanh dong nay la vinh vien va khong the hoan tac.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Nguoi dung' },
        activeMembersTab: { menuAction__remove: 'Xoa nguoi dung' },
        action__invite: 'Moi nguoi dung',
      },
      invitePage: { title: 'Moi nguoi dung moi' },
    },
    signIn: {
      start: {
        title: 'Dang nhap',
        subtitle: 'de tiep tuc den Firmable',
        actionLink__use_phone: 'Su dung so di dong',
      },
    },
    signUp: { start: { subtitle: 'de tiep tuc den Firmable', actionLink: 'Dang nhap' } },
    formFieldLabel__emailAddress: 'Email cong viec',
    formFieldLabel__phoneNumber: 'So di dong',
    roles: { basic_member: 'Tieu chuan' },
  },

  'zh-HK': {
    createOrganization: { title: '建立公司', formButtonSubmit: '建立' },
    formButtonPrimary: '繼續',
    formFieldLabel__organizationSlug:
      '公司唯一識別碼（這是您公司的唯一識別碼，請勿更改。）',
    formFieldInputPlaceholder__organizationSlug: '用於唯一識別您公司的名稱',
    formFieldInputPlaceholder__organizationName: '公司名稱',
    formFieldLabel__organizationName: '公司名稱',
    unstable__errors: {
      form_identifier_exists__phone_number: '此手機號碼已被使用。請嘗試其他號碼。',
      form_param_format_invalid__phone_number: '手機號碼必須為有效的國際格式',
      not_allowed_access: '您的帳戶已暫時被鎖定。請聯絡支援團隊以了解更多資訊。',
      user_banned: '您的帳戶已暫時被鎖定。請聯絡支援團隊以了解更多資訊。',
    },
    organizationList: {
      titleWithoutPersonal: '加入您的公司',
      subtitle: '申請加入現有公司或建立新公司',
      action__createOrganization: '建立新公司',
      action__suggestionsAccept: '申請加入',
    },
    organizationProfile: {
      navbar: { title: '公司', description: '管理您的公司', members: '用戶' },
      start: { headerTitle__members: '用戶', profileSection: { title: '公司檔案' } },
      profilePage: {
        title: '公司檔案',
        dangerSection: {
          leaveOrganization: {
            title: '離開公司',
            messageLine1: '您確定要離開此公司嗎？您將失去對 Firmable 應用程式的存取權限。',
            messageLine2: '此操作是永久性的，無法撤銷。',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: '用戶' },
        activeMembersTab: { menuAction__remove: '移除用戶' },
        action__invite: '邀請用戶',
      },
      invitePage: { title: '邀請新用戶' },
    },
    signIn: {
      start: {
        title: '登入',
        subtitle: '繼續前往 Firmable',
        actionLink__use_phone: '使用手機號碼',
      },
    },
    signUp: { start: { subtitle: '繼續前往 Firmable', actionLink: '登入' } },
    formFieldLabel__emailAddress: '工作電郵',
    formFieldLabel__phoneNumber: '手機號碼',
    roles: { basic_member: '標準' },
  },

  'fil-PH': {
    createOrganization: { title: 'Gumawa ng kumpanya', formButtonSubmit: 'Gumawa' },
    formButtonPrimary: 'Magpatuloy',
    formFieldLabel__organizationSlug:
      'Natatanging pantukoy ng kumpanya (Ito ang natatanging pantukoy ng iyong kumpanya, huwag itong baguhin.)',
    formFieldInputPlaceholder__organizationSlug: 'Isang bagay na makakakilala sa iyong kumpanya nang natatangi',
    formFieldInputPlaceholder__organizationName: 'Pangalan ng kumpanya',
    formFieldLabel__organizationName: 'Pangalan ng kumpanya',
    unstable__errors: {
      form_identifier_exists__phone_number: 'Ginagamit na ang mobile number na ito. Subukan ang iba.',
      form_param_format_invalid__phone_number: 'Ang mobile number ay dapat nasa wastong international na format',
      not_allowed_access: 'Pansamantalang na-lock ang iyong account. Makipag-ugnayan sa support para malaman ang higit pa.',
      user_banned: 'Pansamantalang na-lock ang iyong account. Makipag-ugnayan sa support para malaman ang higit pa.',
    },
    formFieldInputPlaceholder__phoneNumber: '412 345 678',
    organizationList: {
      titleWithoutPersonal: 'Sumali sa iyong kumpanya',
      subtitle: 'Humiling na sumali sa isang umiiral na kumpanya o gumawa ng bago',
      action__createOrganization: 'Gumawa ng bagong kumpanya',
      action__suggestionsAccept: 'Humiling na sumali',
    },
    organizationProfile: {
      navbar: { title: 'Kumpanya', description: 'Pamahalaan ang iyong kumpanya', members: 'Mga User' },
      start: { headerTitle__members: 'Mga User', profileSection: { title: 'Profile ng kumpanya' } },
      profilePage: {
        title: 'Profile ng kumpanya',
        dangerSection: {
          leaveOrganization: {
            title: 'Umalis sa kumpanya',
            messageLine1:
              'Sigurado ka bang gusto mong umalis sa kumpanyang ito? Mawawalan ka ng access sa Firmable app.',
            messageLine2: 'Permanente ang aksyong ito at hindi na mababawi.',
          },
        },
      },
      membersPage: {
        start: { headerTitle__members: 'Mga User' },
        activeMembersTab: { menuAction__remove: 'Alisin ang user' },
        action__invite: 'Mag-imbita ng user',
      },
      invitePage: { title: 'Mag-imbita ng mga bagong user' },
    },
    signIn: {
      start: {
        title: 'Mag-log in',
        subtitle: 'para magpatuloy sa Firmable',
        actionLink__use_phone: 'Gamitin ang mobile number',
      },
    },
    signUp: { start: { subtitle: 'para magpatuloy sa Firmable', actionLink: 'Mag-log in' } },
    formFieldLabel__emailAddress: 'Work email',
    formFieldLabel__phoneNumber: 'Mobile number',
    roles: { basic_member: 'Standard' },
  },
};

function deepMerge(target: Record<string, any>, source: Record<string, any>): Record<string, any> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object' &&
      !Array.isArray(target[key])
    ) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  return result;
}

/**
 * Returns a Clerk localization object for the given app locale.
 * Merges: Clerk base locale (if non-English) + Firmable custom overrides (translated).
 */
export function getClerkLocalization(locale: SupportedLocale): Record<string, any> {
  const baseLocale = CLERK_BASE_LOCALES[locale];
  const overrideKey = locale.startsWith('en') ? 'en' : locale;
  const overrides = FIRMABLE_OVERRIDES[overrideKey] ?? FIRMABLE_OVERRIDES['en'];

  if (!baseLocale) {
    // English variant — just return overrides
    return overrides;
  }

  // Non-English — merge Clerk's base translations with Firmable overrides
  return deepMerge(baseLocale as Record<string, any>, overrides);
}
